import { ref, computed, readonly } from 'vue'
import { createWalletClient, custom, type WalletClient, type Address, type Hash } from 'viem'
import { mainnet, arbitrum, base } from 'viem/chains'

/**
 * Transaction request for sending EVM transactions
 */
export interface TransactionRequest {
  to: string
  data: string
  value: string
  chainId: number
  gasLimit?: string
}

// LocalStorage key for wallet type persistence
const WALLET_TYPE_KEY = 'aurumrun:wallet:type'

// Module-level state for singleton pattern
const address = ref<Address | null>(null)
const isConnected = ref(false)
const isConnecting = ref(false)
const chainId = ref<number | null>(null)
const error = ref<string | null>(null)

// Wallet type detection
type EvmWalletType = 'metamask' | 'coinbase' | 'injected' | null
const walletType = ref<EvmWalletType>(null)

// Wallet client instance
let walletClient: WalletClient | null = null

// EVM provider type for browser extensions
interface EIP1193Provider {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>
  on?: (event: string, handler: (...args: unknown[]) => void) => void
  removeListener?: (event: string, handler: (...args: unknown[]) => void) => void
  isMetaMask?: boolean
  isCoinbaseWallet?: boolean
  isPhantom?: boolean
  // Other wallets that hijack window.ethereum and pretend to be MetaMask for
  // back-compat with dapps that hard-code isMetaMask. Listed so the legacy
  // fallback can exclude them when EIP-6963 discovery isn't available.
  isTrust?: boolean
  isTrustWallet?: boolean
  isBraveWallet?: boolean
  isRabby?: boolean
  // Multiple wallets inject into providers array
  providers?: EIP1193Provider[]
}

// EIP-6963 — wallet self-announcement protocol. Each installed wallet
// dispatches `eip6963:announceProvider` (and re-announces in response to a
// `eip6963:requestProvider` event we dispatch), carrying a stable rdns
// identifier so we can pick the *real* MetaMask even when Trust/Brave/etc.
// hijack `window.ethereum.isMetaMask`.
interface EIP6963ProviderInfo {
  uuid: string
  name: string
  icon: string
  rdns: string
}

interface EIP6963ProviderDetail {
  info: EIP6963ProviderInfo
  provider: EIP1193Provider
}

const KNOWN_RDNS: Record<'metamask' | 'coinbase', string> = {
  metamask: 'io.metamask',
  coinbase: 'com.coinbase.wallet',
}

// Type-safe window access for wallet detection
// Using interface extension in module scope to avoid conflicts with viem's WindowProvider
interface WalletWindow {
  ethereum?: EIP1193Provider
  coinbaseWalletExtension?: EIP1193Provider
  // Phantom exposes its EVM provider separately
  phantom?: {
    ethereum?: EIP1193Provider
  }
}

function getWalletWindow(): WalletWindow {
  return typeof window !== 'undefined' ? (window as unknown as WalletWindow) : {}
}

const detectedWallets = ref<Map<string, EIP6963ProviderDetail>>(new Map())

function handleAnnounce(event: Event): void {
  const detail = (event as CustomEvent<EIP6963ProviderDetail>).detail
  if (detail?.info?.uuid && detail?.provider) {
    detectedWallets.value = new Map(detectedWallets.value).set(detail.info.uuid, detail)
  }
}

// Subscribe at module-load. EIP-6963 spec requires wallets to re-announce in
// response to `requestProvider`, so any announcements that fired before our
// listener was registered are caught by this dispatch — synchronously.
if (typeof window !== 'undefined') {
  window.addEventListener('eip6963:announceProvider', handleAnnounce)
  window.dispatchEvent(new Event('eip6963:requestProvider'))
}

function findAnnouncedByRdns(rdns: string): EIP1193Provider | null {
  for (const detail of detectedWallets.value.values()) {
    if (detail.info.rdns === rdns) return detail.provider
  }
  return null
}

// Tightened legacy MetaMask check for the path where EIP-6963 discovery
// returns nothing. Excludes every known wallet that sets isMetaMask=true
// for back-compat (Trust/Brave/Rabby/Phantom/Coinbase) — without these we'd
// route MetaMask clicks to whichever hijacker won the window.ethereum race.
function isLegacyMetaMask(p: EIP1193Provider | undefined): boolean {
  return (
    !!p?.isMetaMask
    && !p.isPhantom
    && !p.isTrust
    && !p.isTrustWallet
    && !p.isBraveWallet
    && !p.isRabby
    && !p.isCoinbaseWallet
  )
}

interface Eip2255Permission {
  parentCapability: string
  caveats?: Array<{ type: string; value: unknown }>
}

/**
 * Open the wallet's account picker so the user can choose which account to
 * authenticate with. `eth_requestAccounts` alone returns the cached, already-
 * granted account silently — locking login to whatever account was permitted
 * first. `wallet_requestPermissions` (EIP-2255) re-opens the picker on every
 * call, which is the canonical "switch account at login" pattern in MetaMask.
 *
 * Coinbase Wallet does not implement EIP-2255; method-not-supported (and any
 * other non-rejection error) falls back to `eth_requestAccounts`, preserving
 * the pre-existing connect-and-prompt behavior. User rejections (EIP-1193
 * code 4001) are propagated.
 */
async function requestAccountSelection(provider: EIP1193Provider): Promise<Address[]> {
  try {
    const permissions = (await provider.request({
      method: 'wallet_requestPermissions',
      params: [{ eth_accounts: {} }],
    })) as Eip2255Permission[] | null

    const caveat = permissions
      ?.find((p) => p.parentCapability === 'eth_accounts')
      ?.caveats?.find((c) => c.type === 'restrictReturnedAccounts')

    if (Array.isArray(caveat?.value) && caveat.value.length > 0) {
      return caveat.value as Address[]
    }
  } catch (err) {
    if ((err as { code?: number }).code === 4001) throw err
  }
  return (await provider.request({ method: 'eth_requestAccounts' })) as Address[]
}

/**
 * Get chain config by chain ID
 * Defaults to arbitrum for unsupported chains
 */
function getChainById(id: number): typeof mainnet | typeof base | typeof arbitrum {
  if (id === mainnet.id) return mainnet
  if (id === base.id) return base
  return arbitrum
}

/**
 * Composable for EVM wallet connections using viem directly
 *
 * Replaces wagmi with direct viem usage for:
 * - MetaMask (via window.ethereum)
 * - Coinbase Wallet (via window.coinbaseWalletExtension or window.ethereum)
 *
 * Flow:
 * 1. Detect available wallets
 * 2. Connect to selected wallet
 * 3. Sign messages for SIWE
 */
export function useEvmWallet() {
  const { t } = useI18n()

  /**
   * Detect available EVM wallets
   * Properly handles multiple wallet extensions
   */
  const availableWallets = computed(() => {
    const win = getWalletWindow()

    const wallets: Array<{
      type: EvmWalletType
      name: string
      icon: string
      installed: boolean
    }> = []

    // MetaMask — EIP-6963 first (the only way to disambiguate real MetaMask
    // from Trust/Brave/Rabby/Phantom that all set isMetaMask=true).
    const hasMetaMask =
      !!findAnnouncedByRdns(KNOWN_RDNS.metamask) ||
      !!win.ethereum?.providers?.some(isLegacyMetaMask) ||
      isLegacyMetaMask(win.ethereum)
    wallets.push({
      type: 'metamask',
      name: 'MetaMask',
      icon: '/icons/metamask.svg',
      installed: hasMetaMask,
    })

    // Coinbase Wallet — EIP-6963 first, then the legacy dedicated slot /
    // providers array / isCoinbaseWallet flag.
    const hasCoinbase =
      !!findAnnouncedByRdns(KNOWN_RDNS.coinbase) ||
      !!win.coinbaseWalletExtension ||
      !!win.ethereum?.providers?.some((p) => p.isCoinbaseWallet) ||
      win.ethereum?.isCoinbaseWallet === true
    wallets.push({
      type: 'coinbase',
      name: 'Coinbase Wallet',
      icon: '/icons/coinbase.svg',
      installed: hasCoinbase,
    })

    return wallets
  })

  /**
   * Get the appropriate provider for the wallet type
   * Handles multiple wallet extensions competing for window.ethereum
   */
  function getProvider(type: EvmWalletType): EIP1193Provider | null {
    const win = getWalletWindow()

    if (type === 'coinbase') {
      const announced = findAnnouncedByRdns(KNOWN_RDNS.coinbase)
      if (announced) return announced
      if (win.coinbaseWalletExtension) return win.coinbaseWalletExtension
      const fromProviders = win.ethereum?.providers?.find((p) => p.isCoinbaseWallet)
      if (fromProviders) return fromProviders
      if (win.ethereum?.isCoinbaseWallet) return win.ethereum
      return null
    }

    if (type === 'metamask') {
      const announced = findAnnouncedByRdns(KNOWN_RDNS.metamask)
      if (announced) return announced
      const fromProviders = win.ethereum?.providers?.find(isLegacyMetaMask)
      if (fromProviders) return fromProviders
      if (isLegacyMetaMask(win.ethereum)) return win.ethereum ?? null
      return null
    }

    // Default: return ethereum for 'injected' type
    return win.ethereum || null
  }

  /**
   * Handle account changes from wallet
   */
  function handleAccountsChanged(accounts: unknown): void {
    // Validate that accounts is an array before processing
    if (!Array.isArray(accounts)) {
      disconnect()
      return
    }
    const firstAccount = accounts[0] as Address | undefined
    if (!firstAccount) {
      disconnect()
    } else {
      address.value = firstAccount
    }
  }

  /**
   * Handle chain changes from wallet
   */
  function handleChainChanged(newChainId: unknown): void {
    if (typeof newChainId !== 'string') {
      console.warn('[useEvmWallet] Invalid chainId format:', newChainId)
      return
    }

    const parsed = parseInt(newChainId, 16)
    if (Number.isNaN(parsed)) {
      console.warn('[useEvmWallet] Failed to parse chainId:', newChainId)
      return
    }

    chainId.value = parsed
  }

  /**
   * Handle wallet disconnect
   */
  function handleDisconnect(): void {
    disconnect()
  }

  /**
   * Connect to an EVM wallet
   */
  async function connect(type: EvmWalletType = 'metamask'): Promise<Address> {
    if (isConnecting.value) {
      throw new Error('Connection already in progress')
    }

    const provider = getProvider(type)
    if (!provider) {
      const walletName = type === 'metamask' ? 'MetaMask' : 'Coinbase Wallet'
      throw new Error(t('errors.wallet.notInstalled', { wallet: walletName }))
    }

    isConnecting.value = true
    error.value = null

    try {
      const accounts = await requestAccountSelection(provider)

      const firstAccount = accounts?.[0]
      if (!accounts || accounts.length === 0 || !firstAccount) {
        throw new Error('No accounts returned')
      }

      // Get chain ID
      const chainIdHex = (await provider.request({
        method: 'eth_chainId',
      })) as string
      const parsedChainId = parseInt(chainIdHex, 16)

      // Create viem wallet client with correct chain
      walletClient = createWalletClient({
        account: firstAccount,
        chain: getChainById(parsedChainId),
        transport: custom(provider),
      })

      // Update state
      address.value = firstAccount
      chainId.value = parsedChainId
      isConnected.value = true
      walletType.value = type

      // Persist wallet type to localStorage for session restoration
      if (typeof window !== 'undefined' && type) {
        localStorage.setItem(WALLET_TYPE_KEY, type)
      }

      // Listen for account changes
      provider.on?.('accountsChanged', handleAccountsChanged)
      provider.on?.('chainChanged', handleChainChanged)
      provider.on?.('disconnect', handleDisconnect)

      return firstAccount
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : 'Failed to connect wallet'
      throw err
    } finally {
      isConnecting.value = false
    }
  }

  /**
   * Disconnect wallet
   */
  function disconnect(): void {
    const provider = getProvider(walletType.value)

    // Remove event listeners
    if (provider) {
      provider.removeListener?.('accountsChanged', handleAccountsChanged)
      provider.removeListener?.('chainChanged', handleChainChanged)
      provider.removeListener?.('disconnect', handleDisconnect)
    }

    // Clear persisted wallet type from localStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem(WALLET_TYPE_KEY)
    }

    // Reset state
    address.value = null
    chainId.value = null
    isConnected.value = false
    walletType.value = null
    walletClient = null
    error.value = null
  }

  /**
   * Restore wallet connection from localStorage after page refresh
   * Uses eth_accounts (no popup) to check if wallet is still connected
   * Returns true if wallet was successfully restored, false otherwise
   */
  async function restore(): Promise<boolean> {
    if (typeof window === 'undefined') return false

    // Already connected, no need to restore
    if (isConnected.value) return true

    const savedType = localStorage.getItem(WALLET_TYPE_KEY) as EvmWalletType
    if (!savedType) return false

    const provider = getProvider(savedType)
    if (!provider) {
      localStorage.removeItem(WALLET_TYPE_KEY)
      return false
    }

    try {
      // Use eth_accounts (silent, no popup) instead of eth_requestAccounts
      const accounts = (await provider.request({
        method: 'eth_accounts',
      })) as Address[]

      const firstAccount = accounts?.[0]
      if (!accounts || accounts.length === 0 || !firstAccount) {
        // No accounts means user disconnected wallet
        localStorage.removeItem(WALLET_TYPE_KEY)
        return false
      }

      // Get chain ID
      const chainIdHex = (await provider.request({
        method: 'eth_chainId',
      })) as string
      const parsedChainId = parseInt(chainIdHex, 16)

      // Create viem wallet client with correct chain
      walletClient = createWalletClient({
        account: firstAccount,
        chain: getChainById(parsedChainId),
        transport: custom(provider),
      })

      // Update state
      address.value = firstAccount
      chainId.value = parsedChainId
      isConnected.value = true
      walletType.value = savedType

      // Listen for account changes
      provider.on?.('accountsChanged', handleAccountsChanged)
      provider.on?.('chainChanged', handleChainChanged)
      provider.on?.('disconnect', handleDisconnect)

      return true
    } catch (err) {
      console.warn('[useEvmWallet] Failed to restore wallet connection:', err)
      localStorage.removeItem(WALLET_TYPE_KEY)
      return false
    }
  }

  /**
   * Sign a message with the connected wallet
   */
  async function signMessage(message: string): Promise<string> {
    if (!walletClient || !address.value) {
      throw new Error('Wallet not connected')
    }

    const signature = await walletClient.signMessage({
      account: address.value,
      message,
    })

    return signature
  }

  /**
   * Switch the wallet to a different chain
   * Uses wallet_switchEthereumChain RPC call
   */
  async function switchChain(targetChainId: number): Promise<void> {
    if (!isConnected.value) {
      throw new Error('Wallet not connected')
    }

    const provider = getProvider(walletType.value)
    if (!provider) {
      throw new Error('Wallet provider not available')
    }

    try {
      // Convert chainId to hex format required by wallet_switchEthereumChain
      const chainIdHex = `0x${targetChainId.toString(16)}`

      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: chainIdHex }],
      })

      // Update local state after successful switch
      chainId.value = targetChainId

      // Recreate wallet client with new chain
      if (walletClient && address.value) {
        walletClient = createWalletClient({
          account: address.value,
          chain: getChainById(targetChainId),
          transport: custom(provider),
        })
      }
    } catch (err) {
      const switchError = err as { code?: number; message?: string }
      // Error code 4001 means user rejected the request
      if (switchError.code === 4001) {
        error.value = 'User rejected chain switch'
        throw new Error('User rejected chain switch')
      }
      // Error code 4902 means chain is not added to wallet
      if (switchError.code === 4902) {
        error.value = t('errors.wallet.chainNotAvailable')
        throw new Error(t('errors.wallet.chainNotAvailable'))
      }
      error.value = switchError.message || 'Failed to switch chain'
      throw new Error(error.value)
    }
  }

  /**
   * Send a transaction using the connected wallet
   * Handles chain mismatch by switching chains if needed
   * @returns Transaction hash
   */
  async function sendTransaction(tx: TransactionRequest): Promise<Hash> {
    if (!walletClient || !address.value) {
      error.value = 'Wallet not connected'
      throw new Error('Wallet not connected')
    }

    // Check if chain mismatch and switch if needed
    if (chainId.value !== tx.chainId) {
      await switchChain(tx.chainId)
    }

    try {
      const hash = await walletClient.sendTransaction({
        account: address.value,
        to: tx.to as Address,
        data: tx.data as `0x${string}`,
        value: BigInt(tx.value),
        chain: getChainById(tx.chainId),
        ...(tx.gasLimit && { gas: BigInt(tx.gasLimit) }),
      })

      return hash
    } catch (err) {
      const txError = err as { code?: number; message?: string }
      // Error code 4001 means user rejected the transaction
      if (txError.code === 4001) {
        error.value = 'User rejected transaction'
        throw new Error('User rejected transaction')
      }
      error.value = txError.message || 'Failed to send transaction'
      throw new Error(error.value)
    }
  }

  /**
   * Get CAIP-2 formatted chain ID for Privy SIWE
   */
  function getFormattedChainId(): string {
    return `eip155:${chainId.value || 42161}`
  }

  return {
    // State (readonly to prevent external mutation)
    address: readonly(address),
    isConnected: readonly(isConnected),
    isConnecting: readonly(isConnecting),
    chainId: readonly(chainId),
    walletType: readonly(walletType),
    error: readonly(error),
    // Computed
    availableWallets,
    // Methods
    connect,
    disconnect,
    restore,
    signMessage,
    switchChain,
    sendTransaction,
    getFormattedChainId,
  }
}

export const EVM_WALLET_URLS: Record<string, string> = {
  metamask: 'https://metamask.io/download/',
  coinbase: 'https://www.coinbase.com/wallet/downloads',
}
